<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div ref="wyGenaricTable" class="wy-genaric-table" v-if="!(this.tableDataResults.length > 0 && targetsData.length == 0)">
    <div v-if="targetsData.length>0" class="wy-table-wrapper" style="width: 100%; overflow-x: hidden;" :class="tableClass">
      <div class="target-title">
        Target Ranges
        <el-tooltip effect="dark" popper-class="target-table-range-tooltip-popup" placement="bottom">
          <div slot="content">
            For Bot Experience (BES) & Bot Automation (BAS), the upper limit of target ranges would be reflected as overall bot targets. On the gauge chart, the section below the lower limit would be marked as red and above the upper limit would be marked as green.
            <br><br>
            With Cost per Automated Chat ($/AC), the inverse of the above would apply. The lower limit of it’s target range would be adopted as the target value. On the gauge chart, the section above the upper limit would be marked as red and below the lower limit would be marked as green.
          </div>
          <div class="info-icon"></div>
        </el-tooltip>
      </div>
      <div class="target-description">The target ranges for BES, BAS, and $/AC would be used to define gauge chart ranges on the Experience and Automation page. For each metric, the field taken as the target value is highlighted below. Please note, the fields are inversed for $/AC.</div>
      <div class="target-table target-section">
        <div class="target-header-section">
          <div class="target-header bes-target-header">BES</div>
          <div class="target-header bas-target-header">BAS</div>
          <div class="target-header ac-target-header">$/AC</div>
        </div>
        <div class="target-content-section">
          <div class="target-section bes-target">
            <!-- <span v-if="targetsData[0].besTarget.prefix" class="company-target-prefix">{{targetsData[0].besTarget.prefix}}</span> -->
            <wy-target-number-range-2-values v-if="targetsData[0].besTarget&&Object.keys(targetsData[0].besTarget).length>0" ref="besTarget" :controller="module" :rowIndex="0" :tableData="targetsData" :columnName="'besTarget'" :data="targetsData[0].besTarget" />
            <!-- <span class="company-target-suffix" v-if="targetsData[0].besTarget.suffix">{{targetsData[0].besTarget.suffix}}</span> -->
            <span class="cancel-target el-icon-close" :class="{'disabled': targetsData[0].besTarget.minValue===null&&targetsData[0].besTarget.maxValue===null}" @click="removeTargetValue('besTarget', targetsData[0].besTarget)"></span>
          </div>
          <div class="target-section bas-target">
            <!-- <span v-if="targetsData[0].basTarget.prefix" class="company-target-prefix">{{targetsData[0].basTarget.prefix}}</span> -->
            <wy-target-number-range-2-values v-if="targetsData[0].basTarget&&Object.keys(targetsData[0].basTarget).length>0" ref="basTarget" :controller="module" :rowIndex="0" :tableData="targetsData" :columnName="'basTarget'" :data="targetsData[0].basTarget" />
            <!-- <span class="company-target-suffix" v-if="targetsData[0].basTarget.suffix">{{targetsData[0].basTarget.suffix}}</span> -->
            <span class="cancel-target el-icon-close" :class="{'disabled': targetsData[0].basTarget.minValue===null&&targetsData[0].basTarget.maxValue===null}" @click="removeTargetValue('basTarget', targetsData[0].basTarget)"></span>
          </div>
          <div class="target-section ac-target">
            <!-- <span v-if="targetsData[0].acTarget.prefix" class="company-target-prefix">{{targetsData[0].acTarget.prefix}}</span> -->
            <wy-target-number-range-2-values v-if="targetsData[0].acTarget&&Object.keys(targetsData[0].acTarget).length>0" ref="acTarget" :controller="module" :rowIndex="0" :tableData="targetsData" :columnName="'acTarget'" :data="targetsData[0].acTarget" />
            <!-- <span class="company-target-suffix" v-if="targetsData[0].acTarget.suffix">{{targetsData[0].acTarget.suffix}}</span> -->
            <span class="cancel-target el-icon-close" :class="{'disabled': targetsData[0].acTarget.minValue===''&&targetsData[0].acTarget.maxValue===''}" @click="removeTargetValue('acTarget', targetsData[0].acTarget)"></span>
          </div>
        </div>
      </div>

      <div class="target-title">Industry Benchmark</div>
      <div class="target-description">The industry benchmark for BES, BAS and $/AC would be represented in the gauge charts on the Experience and Automation page. If benchmark values are not configured, the benchmark indicator would be hidden from view.</div>
      <div class="target-table benchmark-section">
        <div class="target-header-section">
          <div class="target-header bes-target-header">BES</div>
          <div class="target-header bas-target-header">BAS</div>
          <div class="target-header ac-target-header">$/AC</div>
        </div>
        <div class="target-content-section">
          <div class="benchmark-section bes-target">
            <span v-if="targetsData[0].besBenchmark.prefix">{{targetsData[0].besBenchmark.prefix}}</span>
            <wy-target-number-range-2-values v-if="targetsData[0].besBenchmark&&Object.keys(targetsData[0].besBenchmark).length>0" ref="besBenchmark" :controller="module" :rowIndex="0" :tableData="targetsData" :columnName="'besBenchmark'" :data="targetsData[0].besBenchmark" :type="'targetRange'" />
            <span class="company-target-suffix" v-if="targetsData[0].besBenchmark.suffix">{{targetsData[0].besBenchmark.suffix}}</span>
            <span class="cancel-target el-icon-close" :class="{'disabled': targetsData[0].besBenchmark.value===null}" @click="removeTargetValue('besBenchmark', targetsData[0].besBenchmark)"></span>
          </div>
          <div class="benchmark-section bas-target">
            <span v-if="targetsData[0].basBenchmark.prefix">{{targetsData[0].basBenchmark.prefix}}</span>
            <wy-target-number-range-2-values v-if="targetsData[0].basBenchmark&&Object.keys(targetsData[0].basBenchmark).length>0" ref="basBenchmark" :controller="module" :rowIndex="0" :tableData="targetsData" :columnName="'basBenchmark'" :data="targetsData[0].basBenchmark" :type="'targetRange'" />
            <span class="company-target-suffix" v-if="targetsData[0].basBenchmark.suffix">{{targetsData[0].basBenchmark.suffix}}</span>
            <span class="cancel-target el-icon-close" :class="{'disabled': targetsData[0].basBenchmark.value===null}" @click="removeTargetValue('basBenchmark', targetsData[0].basBenchmark)"></span>
          </div>
          <div class="benchmark-section ac-target">
            <span v-if="targetsData[0].acBenchmark.prefix" class="company-target-prefix">{{targetsData[0].acBenchmark.prefix}}</span>
            <wy-target-number-range-2-values v-if="targetsData[0].acBenchmark&&Object.keys(targetsData[0].acBenchmark).length>0" ref="acBenchmark" :controller="module" :rowIndex="0" :tableData="targetsData" :columnName="'acBenchmark'" :data="targetsData[0].acBenchmark" :type="'targetRange'" />
            <span class="company-target-suffix" v-if="targetsData[0].acBenchmark.suffix">{{targetsData[0].acBenchmark.suffix}}</span>
            <span class="cancel-target el-icon-close" :class="{'disabled': targetsData[0].acBenchmark.value===''}" @click="removeTargetValue('acBenchmark', targetsData[0].acBenchmark)"></span>
          </div>
        </div>
      </div>

      
      
    </div>
    <div v-else class="no-result-found">
      <div class="no-data"></div>
      <div class="no-data-title">{{$t('noResultFound.title')}}</div>
      <div class="no-data-content">
        <div class="content-text">
          {{$t('noResultFound.message')}}
        </div>
      </div>
    </div>

    <el-tooltip
      ref="tableTooltip"
      placement="bottom"
      effect="dark"
      :content="tableTooltipContent">
    </el-tooltip>

    <el-popover
      ref="tablePopover"
      placement="bottom"
      title=""
      trigger="hover"      
      popper-class="wy-table-change-popover"
      :content="tablePopoverContent">
    </el-popover>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import WyUtils from '../../library/wy-utils.js';
import wyTargetNumberRange2Values from './wy-target-number-2-values';

export default {
  name: 'wy-genaric-table',
  props: ['controller', 'enableSelection', 'rowClickEvent', 'defaultSort','hasSubtitle', 'tableColumns', 'tableDataResults', 'mode', 'tableName', 'noDataMsg'],

  components: {
    'wy-target-number-range-2-values': wyTargetNumberRange2Values
  },
  
  data() {
    return {
      module: this,
      iShowTable: false,
      groupColumns: null,
      groupFixedColumns: null,
      sortedColumnName: null,
      sortedType: 1,
      sortCaretHovered: 0,
      tablePopoverContent: null,
      tableTooltipContent: null,
      highlightedIndex: null,
      scrollLeft: 0,
      selectedRowsNum: 0,
      allColsWidth: -1,
      variableColWidth: 350,
      minWidth: 320,


      targetType: '',
      tableClass: '',
      indentList: [
                    'Received positive feedback', 
                    'Received negative feedback', 
                    'Customer requested handoff', 
                    'Automatic handoff after intent-based trigger', 
                    'Candidates for new intents', 
                    'Candidates for existing intents'
                  ],
      isCreated: true
    }
  },

  computed: {
    ...mapGetters(['dataModel', 'tableModel', 'companyConfig']),

    fixedColumns() {
      return this.allColumns.filter((item) => item.fixed)
    },

    allColumns() {
      let list = [];
      if(this.groupColumns) {
        this.groupColumns.forEach(group => {
          let currGroup = group.columns.filter(el => el.selected);
          for (let i = 0; i < currGroup.length-1; i ++)
            currGroup[i].lastChild = false;
          if (currGroup.length)
            currGroup[currGroup.length-1].lastChild = true;
          list = list.concat(currGroup);
        })
      }
      return list;
    },

    targetsData() {
      let list = null;
      let tableResults = [];
    
      let hideMetrics = this.companyConfig ? ((this.companyConfig.optimization || {}).hideMetrics || []) : [];
      let tableKey = (this.tableColumns && this.tableColumns.length > 0) ?this.tableColumns[0].name : "";

      if(this.mode && this.mode == 'generic') {
        tableResults = this.tableDataResults.filter(item => {
          return !hideMetrics.includes(item[tableKey])
        });
      }
      else {
        tableResults = this.tableModel.results.filter(item => {
          return !hideMetrics.includes(item[tableKey])
        });
      }
      list = tableResults;
      
      if(list.length > 0) {
        if(!Object.keys(list[0]).includes('besTarget')) {
          list[0]["besTarget"] = { "minValue": null, "maxValue": null, "prefix": "", "suffix": "" }
        }
        else if(Object.keys(list[0]["besTarget"]).includes('value')) {
          list[0]["besTarget"].minValue = Number(list[0]["besTarget"].value);
          list[0]["besTarget"].maxValue = Number(list[0]["besTarget"].value);
          delete list[0]["besTarget"].value;
        }
        if(!Object.keys(list[0]).includes('basTarget')) {
          list[0]["basTarget"] = { "minValue": null, "maxValue": null, "prefix": "", "suffix": "%" }
        }
        else if(Object.keys(list[0]["basTarget"]).includes('value')) {
          list[0]["basTarget"].minValue = Number(list[0]["basTarget"].value);
          list[0]["basTarget"].maxValue = Number(list[0]["basTarget"].value);
          list[0]["basTarget"].suffix = '%';
          delete list[0]["basTarget"].value;
        }
        if(list[0]["basTarget"].suffix == '') {
          list[0]["basTarget"].suffix = '%'
        }
        if(!Object.keys(list[0]).includes('acTarget')) {
          list[0]["acTarget"] = { "minValue": null, "maxValue": null, "prefix": "$", "suffix": "" }
        }
        else {
          list[0]["acTarget"].prefix = '$'
        }
        if(!Object.keys(list[0]).includes('besBenchmark')) {
          list[0]["besBenchmark"] = { "value": null, "prefix": "", "suffix": "" }
        }
        if(!Object.keys(list[0]).includes('basBenchmark')) {
          list[0]["basBenchmark"] = { "value": null, "prefix": "", "suffix": "%" }
        }
        else {
          list[0]["basBenchmark"].suffix = '%'
        }
        if(!Object.keys(list[0]).includes('acBenchmark')) {
          list[0]["acBenchmark"] = { "value": null, "prefix": "$", "suffix": "" }
        }
        else {
          list[0]["acBenchmark"].prefix = '$'
        }
        // if(this.isCreated) {
        //   this.controller.tableDataClone = JSON.parse(JSON.stringify(list));
        //   this.isCreated = false;
        // }
      }
      return list;
    }
  },

  watch: {
    'dataModel.keyword': function() {
      this.clearSelection();
    },
    targetsData: {
        handler: function(newValue) {
          if(this.isCreated) {
            this.controller.tableDataClone = JSON.parse(JSON.stringify(newValue));
            this.isCreated = false;
          }

          if (newValue.length > 0 && newValue[0].acBenchmark.value === '' && 
            newValue[0].besBenchmark.value === null && newValue[0].basBenchmark.value === null &&
            newValue[0].besTarget.minValue === null && newValue[0].besTarget.maxValue == null && 
            newValue[0].basTarget.minValue == null && newValue[0].basTarget.maxValue == null && 
            newValue[0].acTarget.minValue == '' && newValue[0].acTarget.maxValue == '') { 
            this.controller.disableClearAllBtn = true;
          }
          else {
            this.controller.disableClearAllBtn = false;
          }
        },
        deep: true
    },
  },

  mounted: function() {
    if(this.$refs.wyGenaricTable) {
      this.resizeObserver = new ResizeObserver(this.handleWindowResize).observe(this.$refs.wyGenaricTable);
    }
  },

  updated: function() {
    this.handleWindowResize();
  },

  methods: {
    ...mapMutations(['updateTableModelSelectedRows']),

    handleClick() {
      // do nothing
    },

    cellFormatter(value) {
      if(typeof value == "number") {
        value = WyUtils.formatterNumber(value);
      }
      return value;
    },

    fixedTableHeaderClasses(column) {
      let classes = {'lastChild': column.lastChild, 'sortable': column.sortable, 'fixed': true, 'sorted': this.sortedColumnName==column.sortBy, 
          'ascending': this.sortedType^this.sortCaretHovered==0, 'descending': this.sortedType^this.sortCaretHovered==1};
      classes[column.name] = true;
      return classes;
    },

    handleSort({sortBy, sortType}) {
      if(sortType != null) {
        this.sortedColumnName = sortBy;
        this.sortedType = sortType;
        return;
      }
      if(this.sortedColumnName != sortBy) {
        this.sortedType = 1;  
      }
      else {
        this.sortedType = this.sortedType==0 ? 1 : 0;
      }
      if (this.sortedColumnName != sortBy) {
        this.sortedColumnName = sortBy;
        this.sortCaretHovered = 1;
      }
    },

    handleSelect(e) {
      e.preventDefault();
      e.stopPropagation();
      let selectedRows = this.targetsData.filter(item => item.isSelected);
      this.updateTableModelSelectedRows(selectedRows);
      this.selectedRowsNum = selectedRows.length;
    },

    handleSelectStopPropagation(e) {
      e.stopPropagation();
    },

    handleSelectAll(e) {
      e.preventDefault();
      e.stopPropagation();
      let selectedRows = this.targetsData.filter(item => {item.isSelected=e.target.checked; return item.isSelected});
      this.updateTableModelSelectedRows(selectedRows);
      this.selectedRowsNum = selectedRows.length;
    },

    clearSelection() {
      let selectedRows = this.targetsData.filter(item => {item.isSelected=false; return false});
      this.updateTableModelSelectedRows(selectedRows);
      this.selectedRowsNum = 0;
    },

    handlePopoverOver(e, content) {
      this.$refs.tablePopover.doClose();
      this.tablePopoverContent = content;
      this.$refs.tablePopover.referenceElm = e.target;
      setTimeout(() => {
        this.$refs.tablePopover.doShow();
      }, 300)
    },

    handlePopoverOut() {
      this.tablePopoverContent = null;
      this.$refs.tablePopover.referenceElm = null;
      this.$refs.tablePopover.doClose();
    },

    handleTooltipOver(e, content) {
      this.$refs.tableTooltip.hide();
      this.$refs.tableTooltip.handleClosePopper();
      this.$refs.tableTooltip.referenceElm = e.target;
      this.tableTooltipContent = (!content || content=='') ? 'No data' : content;
      setTimeout(() => {
          this.$refs.tableTooltip.show();
          this.$refs.tableTooltip.handleShowPopper();
          setTimeout(()=>{
            if (this.$refs.tableTooltip.$refs.popper.getBoundingClientRect().x < 0) {
              this.$refs.tableTooltip.hide();
              this.$refs.tableTooltip.handleClosePopper();
            }
          }, 3)
      }, 100)
    },

    handleTooltipOut() {
      this.$refs.tableTooltip.hide();
      this.$refs.tableTooltip.handleClosePopper();
    },

    handleMousewheelOnMainTable(e) {
      // e.preventDefault();
      // e.stopPropagation();
      this.$refs.wyTableBodyFixed.scrollTop += e.deltaY;  // y scroll fixed overlay table
      this.$refs.wyTableBodyMain.scrollTop += e.deltaY;  // y scroll main table
      this.$refs.wyTableBodyMain.scrollLeft += e.deltaX;  // x scroll main table
      this.scrollLeft = Math.max(0, this.$refs.wyTableBodyMain.scrollLeft) * -1;  // x scroll main header
    },

    handleMousewheelOnFixedTable(e) {
      // e.preventDefault();
      // e.stopPropagation();
      this.$refs.wyTableBodyFixed.scrollTop += e.deltaY;  // y scroll fixed overlay table
      this.$refs.wyTableBodyMain.scrollTop += e.deltaY;  // y scroll main table
    },

    handleScrollingOnMainTable(e) {
      // x scroll fixed overlay table with main table
      this.scrollLeft = e.target.scrollLeft * -1;
      // y scroll fixed overlay table with main table
      this.$refs.wyTableBodyFixed.scrollTop = e.target.scrollTop;
    },

    handleScrollingOnFixedTable(e) {
      // Handles the case where the fixed table is scrolled when user searchers keyword using Ctrl + F
      this.$refs.wyTableBodyMain.scrollTop = e.target.scrollTop;
    },

    handleMouseover() {
      // this.highlightedIndex = e.target.closest('tr').sectionRowIndex;
    },

    handleMouseleave() {
      // this.highlightedIndex = null;
    },

    toggleSortCaretHovered(columnName) {
      if (columnName == this.sortedColumnName)
        this.sortCaretHovered = 1 - this.sortCaretHovered;
    },

    refreshTable() {
      // preapare selected columns
      // this.handleSort({sortBy: this.defaultSort.name, sortType: this.defaultSort.type});
      
      let groupMap = {};
      let tableColumns1 = [];
      tableColumns1 = this.tableColumns;
      tableColumns1.forEach(column => {
        column.sortBy = column.sortBy || column.name;
        column.selected = true;   //default selected all columns
        if(column.groupHidden) {
          if(column.selected) {
            groupMap[column.group] = column;
          }
        }
        else {
          if(!groupMap[column.group]) {
            groupMap[column.group] = {
              label: column.group,
              name: column.group.replace(' ', '-').toLowerCase(),
              columns: [],
              type: column.type,
              isIndeterminate: false,
              fixed: column.fixed,
              width: column.minWidth,
              hasIcon: column.hasIcon,
              cssClass: column.cssClass,
              align: column.align,
              checked: false
            };
          }

          if(column.selected) {
            groupMap[column.group].columns.push(column);
            // // add children
            // if(!column.parent) {
            //   this.dataModel.columns.filter(el => el.parent==column.name).forEach(childColumn => {
            //     groupMap[column.group].columns.push(childColumn);
            //   });
            // }
          }
        }        
      });
      let groupList = [];
      let groupFixedList = [];
      Object.keys(groupMap).forEach(key => {
        let group = groupMap[key];
        if((group.columns == undefined || group.columns.length > 0) && group.name!='dynamicfields') {
          // prepare selected colum list
          groupList.push(group);
          
          // calculate the header width and column spans
          group.headerWith = group.columns.reduce((total, item, index) => {total+=item.minWidth+(index>0?20:0); return total;}, 0);
          group.headerSpan = group.columns.filter(el => el.selected).length;
          
          // prepare fixed column list
          let fixedColumns = group.columns.filter((item) => item.fixed);
          if(fixedColumns.length > 0) {
            groupFixedList.push(group)
          }
        }
      });
      this.allColsWidth = 0;
      for (let i = 0; i < groupList.length; i ++) {
        if (!groupList[i].fixed)
          this.allColsWidth += groupList[i].headerWith + 30;
      }
      // this.variableColWidth will be updated in updated() method

      this.controller.loading = true;
      this.iShowTable = false;
      setTimeout(() => {
        this.controller.loading = false;
        this.groupColumns = groupList;
        this.groupFixedColumns = groupFixedList;
        this.iShowTable = true; 
        this.scrollLeft = 0;
      }, 200);
      this.clearSelection();
    },

    handleWindowResize() {
      if(this.$refs.wyTableBodyMain) {
        this.$refs.wyTableBodyFixed.style.height = this.$refs.wyTableBodyMain.clientHeight + "px";
        if (this.allColsWidth >= 0) {
          // The next four lines of code are same as this.variableColWidth = Math.max(350, this.$refs.wyTableBodyMain.clientWidth - this.allColsWidth - 100);
          // except it prevents an oscillation phenomenon in Chrome when there are no other columns and we toggle the sidebar
          // This is done by only updating the variableColWidth when it's different from two steps ago
          let tmp = Math.max(this.minWidth, this.$refs.wyTableBodyMain.clientWidth - this.allColsWidth - 30);
          if (tmp != this.variableColWidthPrev) {
            this.variableColWidthPrev = this.variableColWidth;
            this.variableColWidth = tmp;
          }
        }
      }
    },

    getTarget(rowName) {
      let target = '-';
      if(this.companyConfig && this.companyConfig.optimization && this.companyConfig.optimization.targets) {
        this.getTargetType();
        if(this.targetType != '') {
          let targetObj = this.companyConfig.optimization.targets[this.targetType];
          if(targetObj && targetObj[rowName] && targetObj[rowName].hasOwnProperty("value") && typeof(targetObj[rowName].value) === 'number' && targetObj[rowName].operator){
            let suffix = targetObj[rowName].suffix ? targetObj[rowName].suffix : '';
            target = targetObj[rowName].operator + " " + targetObj[rowName].value + suffix;
          }
        }
      }
      return target;
    },
    
    isShowIcon(value, rowName) {
      let showIcon = "none";
      if(this.companyConfig && this.companyConfig.optimization && this.companyConfig.optimization.targets) {
        this.getTargetType();
        if(this.targetType != '') {
          let targetObj = this.companyConfig.optimization.targets[this.targetType];
          
          if(targetObj && targetObj[rowName] && targetObj[rowName].hasOwnProperty("value") && typeof(targetObj[rowName].value) === 'number' && targetObj[rowName].operator) {
            if(targetObj[rowName].operator == '>' && value > targetObj[rowName].value) {
              showIcon = "green";
            }
            else if(targetObj[rowName].operator == '<' && value < targetObj[rowName].value) {
              showIcon = "green";
            }
            else if(targetObj[rowName].operator == '=' && value == targetObj[rowName].value) {
              showIcon = "green";
            }
            else {
              showIcon = "red";
            }
          }
        }
      }
      return showIcon;
    },
    getTargetType() {
      if(this.controller.targetType) {
        this.targetType = this.controller.targetType;
      }
      else {
        if(this.controller.selectedCompareBy && this.controller.selectedCompareBy != 'date' ) {
          this.targetType = this.controller.selectedCompareBy;
        }
        else if(this.controller.selectedCompareBy && this.controller.selectedCompareBy == 'date' && this.controller.selectedViewDateRange){
          this.targetType = this.controller.selectedViewDateRange;
        }
      }
    },
    removeTargetValue(ref, data) {
      if(this.isCreated) {
        this.isCreated = false
      }
      if(data.minValue || data.minValue==0 || data.maxValue || data.maxValue==0) {
        if(ref == 'acTarget') {
          this.$refs[ref].localData.minValue = '';
          this.$refs[ref].localData.maxValue = '';
        }
        else {
          this.$refs[ref].localData.minValue = null;
          this.$refs[ref].localData.maxValue = null;
        }
      }
      if(data.value || data.value == 0) {
        if(ref == 'acBenchmark') {
          this.$refs[ref].localData.value = '';
          this.$refs[ref].localData.isNull = true;
        }
        else {
          this.$refs[ref].localData.value = null;
        }
        
      }
      this.$refs[ref].handleChangeTarget()
      // updatedTargetList
      if(this.controller.controller && typeof this.controller.controller.hasNoSavedChanges != 'undefined') {
        this.controller.controller.hasNoSavedChanges = true
      }
      setTimeout(() => {
        if(typeof this.controller.controller.hasError != 'undefined' && !this.controller.controller.hasError) {
          this.controller.controller.hasError = document.querySelectorAll('.has-error').length > 0;
        }
      }, 200);
    },
  },

  created() {
    this.tableClass = this.tableName ? this.tableName + '-table' : '';
    this.getTargetType();
    this.isCreated = true;
  }
}
</script>

<style lang="scss">
.wy-app-modal {
  .has-indent{
    position: relative;
    left: 20px;
  }
  .target-dash-style{
    margin-left: 17px;
  }
  .wy-genaric-table {
    width: 100%;
    .wy-body {
      span.cancel-target {
        line-height: 2.3;
        margin-left: 5px;
        cursor: pointer;
      }
      span.company-target-prefix {
        line-height: 2.3;
        margin-left: 5px;
        width: 20px;
      }
    }

    .wy-table-wrapper {
      position: relative;
      min-width: 1022px;
      padding-right: 20px;
      border-top-left-radius: 15px;
      border-top-right-radius: 15px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      background-color: white;
      
      .target-title {
        display: flex;
        font-size: 14px;
        font-weight: 600;
        // padding-top: 30px;
        padding-bottom: 7px;

        .info-icon {
          background-image: url('../../assets/icons/icon_info.svg');
          background-repeat: no-repeat;
          background-size: 70%;
          height: 24px;
          width: 24px;
          margin-top: 2px;
          margin-left: 5px;
        }
      }
      .target-description {
        font-size: 14px;
        font-weight: 400;
        padding-bottom: 10px;
      }
      .target-table {
        border: 1px solid #CAD2DA;
        .target-header-section {
          border-bottom: 1px solid #CAD2DA;
          .target-header {
            width: 320px;
            padding: 15px 0px 15px 20px;
            display: inline-flex;
            font-weight: 600;
          }
        }
        .target-content-section {
          .target-section {
            display: inline-flex;
            width: 300px;
            padding: 20px 20px 40px 20px;
          }
          .benchmark-section {
            display: inline-flex;
            width: 300px;
            padding: 20px 20px 40px 20px;
            .wy-target-number-range {
              width: 115px;
            }
          }
          .el-icon-close.disabled {
            pointer-events: none;
            color: #CCC;
          }
          .el-icon-close:before {
            position: relative;
            top: 8px;
          }
        }
      }
      .target-table.target-section {
        margin-bottom: 30px;
      }
    }

    table {    
      border-spacing: 0px;
      border-collapse: separate;

      font-size: 14px;
    }

    thead {
      display: table;
      table-layout:fixed;
      background-color: #FFF;
      padding: 0px;

      tr{
        display: block;
        th {
          text-align: left;
          color: var(--site-font-secondary-color) !important;
          padding: 9px 10px;
          height: 19px;
          min-height: 19px;
        }
      }

      .wy-header {
        position: relative;
        left: 0px;
        font-size: 15px;
        font-weight: 500;

        th {
          border-right: 1px solid #fff;
          vertical-align: top;
          text-align: center;
        }
      }

      .wy-sub-header {
        position: relative;
        left: 0px;

        th {
          font-size: 13px;
          vertical-align: top;
          text-align: center;
        }
        th.lastChild {
          border-right: 1px solid #fff;
        }
        th.fixed.index label.el-checkbox {
          position: relative;
          bottom: -1px;
        }
        th.sortable::after {  // this reserves space for the up-arrow/down-arrow icon (not shown yet)
          content: "";
          display: inline-block;
          height: 15px;
          width: 15px;
          position: relative;
          top: 4px;
          left: 4px;
          background-repeat: no-repeat;
        }
        th.sortable.sorted.ascending::after {
          background-image: url('../../assets/icons/icon_sort_asc.svg');
        }
        th.sortable.sorted.descending::after {
          background-image: url('../../assets/icons/icon_sort_des.svg');
        }
        th.sortable.sorted.ascending:hover::after {
          background-image: url('../../assets/icons/icon_hover_arrow_up.svg');
        }
        th.sortable.sorted.descending:hover::after {
          background-image: url('../../assets/icons/icon_hover_arrow_down.svg');
        }
        th.sortable:hover {
          background-color: #d1e5fb;
          cursor: pointer;
          color: #003A5D;
          .sort-caret.ascending {
            background-image: url('../../assets/icons/icon_hover_arrow_up.svg') !important;
          }
          .sort-caret.descending {
            background-image: url('../../assets/icons/icon_hover_arrow_down.svg');
          }
        }
        th.sortable:not(.sorted):hover::after {
          background-image: url('../../assets/icons/icon_hover_arrow_down.svg');
        }
        th:not(.sortable) {
          span {
            padding-left: 0;
          }
        }
      }
    }
    
    tbody {
      display: block;
      .compared-by-chan-tr{
        :nth-child(4){
          min-width: 202px !important;
        }
      }
      tr{
        table-layout: fixed;
        display: block;
        cursor: pointer;

        td {
          padding: 1px 10px;
          height: 60px;
          border-bottom: 1px solid #eee;
          background-color: transparent;

          div {
            display: flex;
            justify-content: center;
          }
        }

        .align-right div {
          // margin-left: auto;
          padding-right: 1px;
        }
        .align-right span {
          margin-left: auto;
          padding-right: 1px;
          width: 42px;
          text-align: center;
        }
          .daily-style span{
            padding-right: 28px !important;
          }
          .compared-by-lang-style span{
            padding-right: 5px !important;
          }
          .compared-by-chan-style span{
            padding-right: 10px !important;
          }
        .wy-table-select .el-checkbox__original {
          margin: 0;
        }

        td.last-child {
          border-right: 1px solid transparent;
          .icon-green-check {
            background-image: url('../../assets/icons/icon-green-check.svg') !important;
            width: 18px;
            background-repeat: no-repeat;
            background-position-y: 3px;
            background-position-x: 2px;
            background-size: 63%;
            
          }
          .icon-red-warning {
            background-image: url('../../assets/icons/icon-red-warning.svg') !important;
            width: 18px;
            background-repeat: no-repeat;
            background-position-y: 2px;
            background-position-x:2px;
            background-size: 60%;
          }
    
          .channel-red-icon-style, .isDaily-red-icon-style{
            padding-right: 0px !important;
          }
          .channel-green-check-style, .isDaily-green-icon-style{
            padding-right: 1px !important;
          }
        }

        td.wy-table-tail {
          padding: 0;
        }
      }
    }

    .highlightedRow td {
      background-color: #f5f7fa !important;
    }

    .wy-table-select {
      min-width: 20px;
      position: relative;
    }

    .wy-table-tail {
      min-width: 22px;
    }
    th.wy-table-tail {
      padding: 9px 7px 9px 10px;
    }

    .wy-table-main {
      width: 100%;
      max-width: 100%;

      thead {
        width: fit-content;
        
        th {
          background-color: #FFF;
          // padding-right: 5px;

        }
        th:last-child, th:nth-last-child(2) {
          border-right: 1px solid transparent;
        }
        th:last-child, th:nth-last-child(1) {
          border-right: 1px solid transparent;
        }
        tr {
          // float: left;  // THIS PLACE IS WHERE SAFARI MESSED UP WHEN YOU ZOOM OUT THE PAGE (BUT CHROME AND FIREFOX DIDN'T)
          width: calc( 100vw - 400px);
          :first-child {
            padding-left: 18px;  // adding the same amt of padding in fixed table
          }
        }
        .wy-header {
          span {
            padding-left: 5px;
          }
          
        }
      }
      tbody { 
        height: calc( 100vh - 245px );
        overflow: auto;
        width: 100%;
        max-width: 100%;
        tr {
          // float: left;  // THIS PLACE IS WHERE SAFARI MESSED UP WHEN YOU ZOOM OUT THE PAGE (BUT CHROME AND FIREFOX DIDN'T)
          width: calc( 100vw - 400px );
          td:first-child {
            padding-left: 20px;  // adding the same amt of padding as in fixed table
          }
          td span.positive-change {
            color: #4448A2;
          }
          td span.negative-change {
            color: #3E6891;
          }
          td span.zero-change {
            color: grey;
          }
        }
        border-right: 1px solid #eee;
        border-bottom: 1px solid #eee;
      }
    }
    // .wy-table-fixed.show-fixed-table-shadow {
    //   // -webkit-box-shadow: 5px -2px 10px -10px grey; */
    //   /* box-shadow: 5px -2px 10px -10px grey; */
    //   box-shadow: 3px 3px 12px #9d9cc759;
    //   background-color: white;
    //   /* border: 1px solid #ddd; */
    //   padding: 0px 6px;
    // }
    .wy-table-fixed {
      position: absolute;
      top: 0px;
      text-align: center;
      // border-left: 1px solid #eee;
      //z-index: 2;
      thead {
        th:nth-last-child(1) {  // corresponding to name colomn in fixed table
          padding-left: 30px;
          text-align: left !important;
        }
      }
      tbody {
        height: calc( 100vh - 270px);
        overflow: auto;
        -ms-overflow-style: none;  // hiding scrollbar on IE
        scrollbar-width: none;     // hiding scrollbar on Firefox
                                  // see below for Chrome
        width: fit-content;
        td:nth-last-child(1) {    // corresponding to name colomn in fixed table
          padding-left: 30px;
          font-weight: 500;
          div {
            justify-content: flex-start;
          }
        }

        tr td:first-child {
          background-color: #F7F8F8;
          div{
            justify-content: unset !important;
          }
        }
      }
      tbody::-webkit-scrollbar {    // hiding scrollbar on Chrome
        display: none;
      }
      input {
        margin: 3px;
      }
      .total-topic-num {
        font-size: 12px;
      }
    }

    .el-no-data {
      cursor: pointer;
      background-image: url('../../assets/icons/icon_comparison_data_not_available.svg') !important;
      background-size: 7%;
      background-repeat: no-repeat;
      background-position-y: 2px;
      background-position-x: 1px;
      background-color: transparent !important;
      height: 20px;
      width: 20px;
      border: 0px;
      margin-left: 0px;
    }

    .el-no-data:hover {
      background-color: unset;
    }

    .icon-number {
      background-color: #80a6cd;
      border-radius: 10px;
      border: 1px solid #FFF;
      height: 16px;
      width: 16px;
      position: relative;
      top: -10px;
      left: 8px;
      font-size: 9px;
      padding-top: 1px;
      padding-left: 0px;
      line-height: initial;
      color: #FFF;
      text-align: center;
    }

    .small-font {
      font-size: 6px !important;
      padding-top: 3px !important;
    }

    .wy-table-icon {
      display: inline-block;
      width : 25px;
      height: 25px;
      background-repeat: no-repeat;
      background-size: 55%;
      position: relative;
      top: 10px;
    }

    .wy-table-change-popover.el-popover--plain {
        padding: 8px 10px;
        border-radius: 5px;
        background-color: var(--site-font-chart-color);
        color: #FFF;
        font-size: 12px;
      .popper__arrow {
        filter: invert(1);
      }
    }

    .wy-table-alert-popover.el-popover--plain {
      background: #80A6CD;
      color: #FFF;
      border-radius: 10px;
      padding: 5px 10px;
      display: inline-block;
      .popper__arrow {
        display: none;
      }
    }
  }
}


.target-table-range-tooltip-popup {
  width: 342px;
}
</style>